import { useEffect, useState } from 'react';
import { ConfigProvider, Descriptions, Input, Tooltip, message } from 'antd';
import { CloseOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';
import { useSelected } from '../../../../providers/selectedContext';
import { useTheme } from '../../../../providers/theme';
import { getColors } from '../../../utils/getColors';
import { updateSample } from '../../../../api/data';
import { UpdateSamplePayload } from '../../../../@types/samples/types';
import { Accordion } from '../../../../design-system/accordion';
import { useAppContext } from '../../../../providers/AppContext';
import { useNotificationAnchor } from '../../../../providers/NotificationCenter';
import AnalysisGasOil from '../analysis/gasOil/analysis';
import GasOilKrValues from '../krValues/gasOil/krValues';
import GasOilRefinement from '../refinement/gasOil/refinement';
import { SgnKrgRegressionAnalysis, SonKroGasRegressionAnalysis } from '../../features';

const toNumberOrNull = (value: unknown): number | null => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

type GasOilVars = {
  Permeability: string;
  Porosity: string;
  Ko: string;
  Krg: string;
  Swi: string;
};

const SUFFIXES: Record<keyof GasOilVars, string> = {
  Permeability: 'mD',
  Porosity: 'fraction',
  Ko: 'mD',
  Krg: 'fraction',
  Swi: 'fraction',
};

const GasOilPanel = ({ activeTab }: { activeTab?: string }) => {
  const { data, updateData } = useAppContext();
  const { selectedInfo, setSelectedInfo } = useSelected();
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const sampleData = selectedInfo?.sample;
  const refinementAnchor = useNotificationAnchor(
    'special-core-gas-oil-refinement'
  );

  const [edit, setEdit] = useState(false);
  const [vars, setVars] = useState<GasOilVars>({
    Permeability: '',
    Porosity: '',
    Ko: '',
    Krg: '',
    Swi: '',
  });
  const [originalVars, setOriginalVars] = useState<GasOilVars>(vars);

  useEffect(() => {
    if (!sampleData) return;
    const loaded: GasOilVars = {
      Permeability: String(sampleData.input_sample_values.permeability ?? ''),
      Porosity: String(sampleData.input_sample_values.porosity ?? ''),
      Ko: String(sampleData.input_sample_values.ko ?? ''),
      Krg: String(sampleData.input_sample_values.krg ?? ''),
      Swi: String(sampleData.input_sample_values.swi ?? ''),
    };
    setVars(loaded);
    setOriginalVars(loaded);
    setEdit(false);
  }, [sampleData]);

  if (!sampleData) return null;

  const hasGasOilLabCurves = (sampleData.input_sample_curves?.gas_oil?.kro_lab?.length ?? 0) > 0;
  const hasGasOilKrAnalysis = !!sampleData.k_rel_curves?.gas_oil;
  const REQUIRED_FIELDS: Array<keyof GasOilVars> = ['Permeability', 'Porosity'];

  const handleSave = async () => {
    try {
      const payload: UpdateSamplePayload = {
        sample_uid: sampleData.uid,
        input_sample_values: {
          permeability: toNumberOrNull(vars.Permeability),
          porosity: toNumberOrNull(vars.Porosity),
          ko: toNumberOrNull(vars.Ko),
          krg: toNumberOrNull(vars.Krg),
          swi: toNumberOrNull(vars.Swi),
        },
      };
      const response = await updateSample(payload);
      const updated = data.wellSamples.map(s =>
        s.uid === response.uid ? response : s
      );
      updateData({ wellSamples: updated });
      setSelectedInfo({ sample: response });
      setOriginalVars(vars);
      setEdit(false);
      message.success('Gas-Oil variables saved.');
    } catch (error: any) {
      console.error('Gas-Oil variables update error ===>', error);
      const apiMessage = error?.response?.data?.message || error?.message;
      message.error(apiMessage || 'Error saving Gas-Oil variables. Please check your inputs and try again.');
    }
  };

  const editIcons = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }} onClick={e => e.stopPropagation()}>
      {edit ? (
        <>
          <Tooltip title="Save">
            <SaveOutlined
              onClick={handleSave}
              style={{ color: colors.colorPrimary, cursor: 'pointer', fontSize: '16px' }}
            />
          </Tooltip>
          <Tooltip title="Cancel">
            <CloseOutlined
              onClick={() => {
                setVars(originalVars);
                setEdit(false);
              }}
              style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: '16px' }}
            />
          </Tooltip>
        </>
      ) : (
        <Tooltip title="Edit">
          <EditOutlined
            onClick={() => {
              setOriginalVars(vars);
              setEdit(true);
            }}
            style={{ color: colors.text, cursor: 'pointer', fontSize: '16px' }}
          />
        </Tooltip>
      )}
    </div>
  );

  return (
    <div>
      {/* Tab: Sample Measurements — Variables + Laboratory Data */}
      {(!activeTab || activeTab === 'sample-measurements') && (
        <>
          <Accordion defaultOpen="gas-oil-vars">
            <Accordion.Item
              id="gas-oil-vars"
              header={
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontWeight: 'bold' }}>
                  <span>Gas-Oil Variables</span>
                  {editIcons}
                </div>
              }
            >
            <ConfigProvider
              theme={{
                components: {
                  Descriptions: {
                    colorBgBase: colors.bg,
                    contentColor: colors.text,
                    labelBg: isDay ? '#fafafa' : '#090909',
                    colorBorder: colors.border,
                  },
                },
                token: {
                  colorText: colors.text,
                  colorBorder: colors.border,
                  colorBgBase: colors.bg,
                  colorPrimaryHover: colors.colorPrimary,
                  colorPrimary: colors.colorPrimary,
                },
              }}
            >
              <Descriptions
                layout="vertical"
                column={5}
                className={
                  isDay
                    ? 'special-core-descriptions-day'
                    : 'special-core-descriptions-night'
                }
              >
                {(Object.keys(vars) as Array<keyof GasOilVars>).map(key => (
                  <Descriptions.Item
                    key={key}
                    label={
                      <span
                        style={{
                          color: isDay ? colors.text : '#FAFBFF',
                          paddingTop: '0.5rem',
                          paddingLeft: '0.5rem',
                        }}
                      >
                        {key}
                        <span style={{ opacity: 0.5, marginLeft: 4 }}>
                          ({SUFFIXES[key]})
                        </span>
                        {key === 'Swi' && (
                          <div style={{ fontSize: '0.7rem', color: '#b7950b', marginTop: 2 }}>
                            If empty, min(Sg) is used
                          </div>
                        )}
                      </span>
                    }
                    contentStyle={{ fontWeight: 'bold', padding: '0.2rem 0.5rem 0.5rem' }}
                  >
                    {edit ? (
                      <Input
                        value={vars[key]}
                        style={{ width: 100 }}
                        size="small"
                        onChange={e =>
                          setVars(prev => ({ ...prev, [key]: e.target.value }))
                        }
                      />
                    ) : (() => {
                      const isMissing = !vars[key] || vars[key] === '';
                      const isRequired = REQUIRED_FIELDS.includes(key);
                      return (
                        <span style={{ color: isRequired && isMissing ? '#ff4d4f' : 'inherit', fontWeight: isRequired && isMissing ? 600 : 'bold' }}>
                          {isMissing ? (isRequired ? '⚠ Required' : '--') : vars[key]}
                        </span>
                      );
                    })()}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </ConfigProvider>
            </Accordion.Item>
          </Accordion>

          <div style={{ marginTop: '1rem' }}>
            <AnalysisGasOil />
          </div>
        </>
      )}

      {/* Tab: Refinement Variables — Find True Sorg + Finding True Krg */}
      {(!activeTab || activeTab === 'refinement-variables') && (
        hasGasOilLabCurves ? (
          <>
            <div>
              <SonKroGasRegressionAnalysis />
            </div>
            <div style={{ marginTop: '1rem' }}>
              <SgnKrgRegressionAnalysis />
            </div>
          </>
        ) : activeTab === 'refinement-variables' ? (
          <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', opacity: 0.65, fontSize: '0.875rem', border: '1px dashed', borderRadius: 6 }}>
            Enter lab data in <strong>Sample Measurements</strong> and click <strong>Calculate</strong> to enable refinement variables.
          </div>
        ) : null
      )}

      {/* Tab: Corey Exponents — No & Ng values */}
      {(!activeTab || activeTab === 'corey-exponents') && (
        hasGasOilKrAnalysis ? (
          <GasOilKrValues />
        ) : activeTab === 'corey-exponents' ? (
          <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', opacity: 0.65, fontSize: '0.875rem', border: '1px dashed', borderRadius: 6 }}>
            Lab curves saved. Click <strong>Calculate</strong> in <strong>Sample Measurements</strong> to compute No &amp; Ng values.
          </div>
        ) : null
      )}

      {/* Tab: Refinement Curves — Refinement */}
      {(!activeTab || activeTab === 'refinement-curves') && (
        hasGasOilKrAnalysis ? (
          <div ref={refinementAnchor} tabIndex={-1}>
            <GasOilRefinement />
          </div>
        ) : activeTab === 'refinement-curves' ? (
          <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', opacity: 0.65, fontSize: '0.875rem', border: '1px dashed', borderRadius: 6 }}>
            Lab curves saved. Click <strong>Calculate</strong> in <strong>Sample Measurements</strong> to enable refinement curves.
          </div>
        ) : null
      )}
    </div>
  );
};

export default GasOilPanel;
